import React, { useMemo } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';

import { mergeLiveDocI18n } from '@confluence/live-pages-utils/entry-points/mergeLiveDocI18n.ts';
import { fg } from '@confluence/feature-gating';

import { i18n as typeError } from '../components/Modals/FileTypeModal.tsx';
import { i18n as uploadCommonError } from '../components/Modals/CommonErrorModal.tsx';
import { FILE_UPLOAD_LIMIT_MB } from '../config/fileUploadLimit.ts';
import { fixedBytesToMegabytes } from '../utils/fixedBytesToMegabytes.ts';
import { ProcessErrors } from '../config/processErrors.ts';
import { GOOGLE_EXPORT_ERRORS } from '../config/googleExportErrors.ts';

import type { Doc } from './useUploadReducer.ts';
import { DocErrors } from './useUploadReducer.ts';

const i18nBase = defineMessages({
	operationAborted: {
		id: 'import-from-sources.error-message.operationAborted',
		defaultMessage: 'Import canceled',
		description: 'Error message indicating that operation was canceled',
	},
	fileExceedsLimitLocalBulk: {
		id: 'import-from-sources.error-message.fileExceedsLimitLocalBulk',
		defaultMessage:
			'This file is {fileSize} MB which exceeds the {uploadFileLimitMb} MB limit. You could break the content into smaller files and import them separately.',
		description: "Message indicating that the file's size is bigger than the limit",
	},
	fileExceedsLimitGdBulk: {
		id: 'import-from-sources.error-message.fileExceedsLimitGdBulk',
		defaultMessage: 'This file exceeds the 10 MB export limit from Google Drive.',
		description: "Message indicating that the file's size is bigger than the limit",
	},
	pageWithThisNameAlreadyExists: {
		id: 'import-from-sources.error-message.pageWithThisNameAlreadyExists',
		defaultMessage:
			'A page with this name already exists. Page names have to be unique within a space. Give your page a different name to publish it.',
		description: 'Message indicating that page with this name already exists',
	},
	liveDocWithThisNameAlreadyExists: {
		id: 'import-from-sources.error-message.liveDocWithThisNameAlreadyExists',
		defaultMessage:
			'A live doc with this name already exists. Titles have to be unique within a space. Give your doc a different name.',
		description: 'Message indicating that live doc with this name already exists',
	},
	imageErrorProcessWarningMessage: {
		id: 'import-from-sources.error-message.imageErrorProcessWarningMessage',
		defaultMessage:
			'Some images couldn’t be imported. We ran into an issue importing one or more of the images in your document. We’ve created placeholders for them in your new page.',
		description: "Text of 'process warning' message in case of image process failure",
	},
	elementErrorProcessWarningMessage: {
		id: 'import-from-sources.error-message.elementErrorProcessWarningMessage',
		defaultMessage:
			'Some elements couldn’t be imported. We ran into an issue importing some elements of your document (images, fonts, etc.).',
		description: "Text of 'process warning' message in case of ADF elements conversion failure",
	},
	elementErrorUserRateLimitMessage: {
		id: 'import-from-sources.error-message.elementErrorUserRateLimitMessage',
		defaultMessage:
			'We had trouble importing this file. Finish this import and retry this upload in a few minutes.',
		description:
			"Text of 'user rate limit error' message in case of user reached the limit for importing files",
	},
	strictFormatErrorMessage: {
		id: 'import-from-sources.error-message.strictFormatErrorMessage',
		defaultMessage:
			'File format not supported. The .docx file imported was saved as a Strict Open XML. Please save it as Word Document .docx and import again.',
		description: 'Error message indicating that a file was saved in a strict format',
	},
	payloadTooLargeErrorMessage: {
		id: 'import-from-sources.error-message.payloadTooLargeErrorMessage',
		defaultMessage:
			'The content is too long. You can split the content into smaller files and import them separately.',
		description: 'Error message indicating that a file is too big to be imported',
	},
});

const i18nLiveDocs = defineMessages({
	imageErrorProcessWarningMessage: {
		id: 'import-from-sources.error-message.imageErrorProcessWarningMessage.livedocs',
		defaultMessage:
			'Some images couldn’t be imported. We ran into an issue importing one or more of the images in your document. We’ve created placeholders for them.',
		description:
			'Updated version of import-from-sources.error-message.imageErrorProcessWarningMessage',
	},
});

export const i18n: {
	operationAborted: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	fileExceedsLimitLocalBulk: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	fileExceedsLimitGdBulk: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	pageWithThisNameAlreadyExists: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	liveDocWithThisNameAlreadyExists: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	imageErrorProcessWarningMessage: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	elementErrorProcessWarningMessage: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	elementErrorUserRateLimitMessage: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	strictFormatErrorMessage: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	payloadTooLargeErrorMessage: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = mergeLiveDocI18n(i18nBase, i18nLiveDocs);

const getErrorMessage = (doc: Doc) => {
	const ERROR_MESSAGES = {
		[DocErrors.TYPE_ERROR]: <FormattedMessage {...typeError.uploadErrorPopupTitleTYPE} />,
		[DocErrors.SIZE_LOCAL_ERROR]: (
			<FormattedMessage
				{...i18n.fileExceedsLimitLocalBulk}
				values={{
					fileSize: fixedBytesToMegabytes(doc.size ?? 0),
					uploadFileLimitMb: FILE_UPLOAD_LIMIT_MB,
				}}
			/>
		),
		[DocErrors.SIZE_GD_ERROR]: <FormattedMessage {...i18n.fileExceedsLimitGdBulk} />,
		[DocErrors.FAILED_UPLOAD_ERROR]: (
			<FormattedMessage {...uploadCommonError.uploadErrorPopupContent} />
		),
		[DocErrors.REQUEST_ABORTED_ERROR]: <FormattedMessage {...i18n.operationAborted} />,
		[DocErrors.NOT_UNIQ_TITLE]: fg('confluence_live_pages_open_beta_trait_opted_in') ? (
			<FormattedMessage {...i18n.liveDocWithThisNameAlreadyExists} />
		) : (
			<FormattedMessage {...i18n.pageWithThisNameAlreadyExists} />
		),
		[ProcessErrors.PROCESS_IMAGE_ERROR]: (
			<FormattedMessage {...i18n.imageErrorProcessWarningMessage} />
		),
		[ProcessErrors.ADF_CONVERSION_ERROR]: (
			<FormattedMessage {...i18n.elementErrorProcessWarningMessage} />
		),
		[GOOGLE_EXPORT_ERRORS.USER_RATE_LIMIT]: (
			<FormattedMessage {...i18n.elementErrorUserRateLimitMessage} />
		),
		[DocErrors.STRICT_FORMAT_ERROR]: <FormattedMessage {...i18n.strictFormatErrorMessage} />,
		[DocErrors.PAYLOAD_TOO_LARGE]: <FormattedMessage {...i18n.payloadTooLargeErrorMessage} />,
	};

	return ERROR_MESSAGES[doc.error!.type];
};

export const useShowUploadErrors = (doc: Doc): any => {
	const errMessage = useMemo(() => getErrorMessage(doc), [doc]);
	return errMessage;
};
